import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'clientTime',
  standalone: false,
})
export class ClientTimePipe implements PipeTransform {
  // Intl reliably returns DST-aware abbreviations (EST/EDT, CST/CDT, MST/MDT,
  // PST/PDT, etc.) for unambiguous zones like US zones — no manual map needed
  // for those, and it correctly flips with daylight saving automatically.
  //
  // For AMBIGUOUS zones (e.g. Asia/Kolkata could mean India/Israel/Ireland),
  // ICU deliberately refuses to guess and returns a generic "GMT+5:30"
  // instead. This map only covers those known-ambiguous exceptions.
  private static readonly ambiguousZoneOverrides: Record<string, string> = {
    'Asia/Kolkata': 'IST',
    'Asia/Calcutta': 'IST', // Windows ICU legacy alias for India — same zone as Asia/Kolkata
  };

  transform(value: string | Date | null | undefined): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(date);

    const part = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find((segment) => segment.type === type)?.value ?? '';

    const month = part('month');
    const day = part('day');
    const year = part('year');
    const hour = part('hour');
    const minute = part('minute');
    const dayPeriod = part('dayPeriod').toUpperCase();

    const zoneLabel = this.resolveZoneLabel(date, timeZone);

    return `${month}/${day}/${year} ${hour}:${minute} ${dayPeriod} ${zoneLabel}`;
  }

  private resolveZoneLabel(date: Date, timeZone: string): string {
    const shortName = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((segment) => segment.type === 'timeZoneName')?.value;

    // Intl gave a real abbreviation (EST/EDT/CST/CDT/PST/PDT/etc.) — use it as is.
    if (shortName && !/^GMT[+-]/.test(shortName)) {
      return shortName;
    }

    // Intl fell back to a generic offset — this zone is ambiguous to ICU.
    // Use our manual override if we have one, else show a clean offset.
    if (ClientTimePipe.ambiguousZoneOverrides[timeZone]) {
      return ClientTimePipe.ambiguousZoneOverrides[timeZone];
    }

    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
      })
        .formatToParts(date)
        .find((segment) => segment.type === 'timeZoneName')?.value ?? timeZone
    );
  }
}
